import type { Deck, DeckEntry } from "./api";

const ENTRY_RE = /^\s*(\d+)\s+(.+?)(?:\s+\(([A-Za-z0-9]{2,5})\)\s+(\S+))?\s*$/;

function lineFor(e: DeckEntry): string {
  const set = e.set ? ` (${e.set.toUpperCase()})` : "";
  const num = e.collector_number ? ` ${e.collector_number}` : "";
  return `${e.count} ${e.name}${set}${num}`;
}

/** Parses the Arena-style text users paste on upload. This intentionally
 *  mirrors the permissive server parser closely enough for preview UI:
 *  validation still belongs to the server, but a local parse lets the
 *  streaming analysis tiles render immediately instead of waiting for save. */
export function parseArenaDeckText(text: string): Deck | null {
  const mainboard: DeckEntry[] = [];
  const sideboard: DeckEntry[] = [];
  let section: "mainboard" | "sideboard" = "mainboard";

  // Match server normalization: Arena copy-to-clipboard sometimes writes
  // DFC/split names with single-slash (`X / Y`) but Scryfall indexes them
  // with double-slash (`X // Y`). See web/server/routes/upload-deck.ts.
  const normalized = text.replace(/\s+\/\s+/g, " // ");

  for (const rawLine of normalized.split(/\r?\n/)) {
    const line = rawLine.trim();
    if (!line) continue;
    if (/^(deck|mainboard)$/i.test(line)) {
      section = "mainboard";
      continue;
    }
    if (/^sideboard$/i.test(line)) {
      section = "sideboard";
      continue;
    }

    const match = ENTRY_RE.exec(line);
    if (!match) continue;
    const entry: DeckEntry = {
      count: Number.parseInt(match[1]!, 10),
      name: match[2]!.trim(),
    };
    if (entry.name.length > 80 || /[{}]/.test(entry.name)) continue;
    if (match[3]) entry.set = match[3].toLowerCase();
    if (match[4]) entry.collector_number = match[4];
    (section === "mainboard" ? mainboard : sideboard).push(entry);
  }

  if (mainboard.length === 0) return null;
  return { format: "standard", mainboard, sideboard };
}

/** Serializes a parsed deck to MTGA-paste-friendly text. When `name`
 *  is provided, prepends an `About / Name …` block so Arena imports
 *  the deck with a real title instead of "Imported Deck". Mainboard
 *  follows under a `Deck` header (required when `About` is present),
 *  then a blank line and `Sideboard`. */
export function serializeDeckForArena(deck: Deck, name?: string): string {
  const out: string[] = [];
  const trimmed = name?.trim();
  if (trimmed) {
    out.push("About", `Name ${trimmed}`, "", "Deck");
  }
  for (const e of deck.mainboard) out.push(lineFor(e));
  if (deck.sideboard.length > 0) {
    out.push("", "Sideboard");
    for (const e of deck.sideboard) out.push(lineFor(e));
  }
  return out.join("\n");
}
